import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { apiService } from '../../services/api';
import { TrialInfo } from '../../types';
import './Admin.css';
import { AdminSpecialistPrompts } from './AdminSpecialistPrompts';

interface OrgResult {
  organizationId: string;
  name: string;
  slug: string;
  planId: string;
  ownerId: string;
  ownerEmail?: string;
  ownerName?: string;
  status: string;
  trial?: TrialInfo;
  createdAt: string;
}

export const Admin: React.FC = () => {
  const navigate = useNavigate();
  const { isSuperAdmin, logout } = useAuth();
  const [activeTrials, setActiveTrials] = useState<OrgResult[]>([]);
  const [searchResults, setSearchResults] = useState<OrgResult[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedOrgDetails, setSelectedOrgDetails] = useState<OrgResult | null>(null);
  const [copiedOwnerEmail, setCopiedOwnerEmail] = useState(false);

  // Modal states
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<OrgResult | null>(null);
  const [trialMaxUsers, setTrialMaxUsers] = useState(5);
  const [trialMaxLeads, setTrialMaxLeads] = useState(500);
  const [trialMaxWhatsApp, setTrialMaxWhatsApp] = useState(2);

  const loadActiveTrials = useCallback(async () => {
    try {
      const data = await apiService.getActiveTrials();
      setActiveTrials(data.organizations);
    } catch (err) {
      console.error('Error loading trials:', err);
    }
  }, []);

  useEffect(() => {
    if (!isSuperAdmin) {
      navigate('/dashboard');
      return;
    }

    const init = async () => {
      setLoading(true);
      await loadActiveTrials();
      setLoading(false);
    };
    init();
  }, [isSuperAdmin, navigate, loadActiveTrials]);

  useEffect(() => {
    setCopiedOwnerEmail(false);
  }, [selectedOrgDetails?.organizationId]);

  const handleSearch = async (): Promise<OrgResult[]> => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return [];
    }
    try {
      setSearchLoading(true);
      const data = await apiService.searchOrganizations(searchTerm);
      setSearchResults(data.organizations);
      return data.organizations;
    } catch (err) {
      console.error('Error searching:', err);
      return [];
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleOpenActivateModal = (org: OrgResult) => {
    setSelectedOrg(org);
    setTrialMaxUsers(5);
    setTrialMaxLeads(500);
    setTrialMaxWhatsApp(2);
    setShowActivateModal(true);
  };

  const handleActivateTrial = async () => {
    if (!selectedOrg) return;

    try {
      setActionLoading(true);
      await apiService.activateTrial(selectedOrg.organizationId, {
        maxUsers: trialMaxUsers,
        maxLeadsPerMonth: trialMaxLeads,
        maxWhatsAppAccounts: trialMaxWhatsApp,
      });
      setShowActivateModal(false);
      setSelectedOrg(null);
      await loadActiveTrials();
      // Refresh search results to reflect change
      if (searchTerm) {
        const organizations = await handleSearch();
        const updatedOrg = organizations.find(org => org.organizationId === selectedOrg.organizationId);
        if (updatedOrg) {
          setSelectedOrgDetails(updatedOrg);
        }
      }
    } catch (err: any) {
      alert(err.response?.data?.message || 'Erro ao ativar trial');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRenewTrial = async (orgId: string) => {
    if (!window.confirm('Renovar trial por mais 3 meses?')) return;

    try {
      setActionLoading(true);
      const data = await apiService.renewTrial(orgId);
      await loadActiveTrials();
      if (selectedOrgDetails?.organizationId === orgId) {
        setSelectedOrgDetails(prev => prev ? { ...prev, trial: data.trial } : prev);
      }
      if (searchTerm) {
        const organizations = await handleSearch();
        const updatedOrg = organizations.find(org => org.organizationId === orgId);
        if (updatedOrg) {
          setSelectedOrgDetails(updatedOrg);
        }
      }
    } catch (err: any) {
      alert(err.response?.data?.message || 'Erro ao renovar trial');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeactivateTrial = async (orgId: string) => {
    if (!window.confirm('Desativar trial? A org voltara aos limites do plano gratuito.')) return;

    try {
      setActionLoading(true);
      await apiService.deactivateTrial(orgId);
      await loadActiveTrials();
      if (selectedOrgDetails?.organizationId === orgId) {
        setSelectedOrgDetails(prev => prev ? { ...prev, trial: undefined } : prev);
      }
      if (searchTerm) {
        const organizations = await handleSearch();
        const updatedOrg = organizations.find(org => org.organizationId === orgId);
        if (updatedOrg) {
          setSelectedOrgDetails(updatedOrg);
        }
      }
    } catch (err: any) {
      alert(err.response?.data?.message || 'Erro ao desativar trial');
    } finally {
      setActionLoading(false);
    }
  };

  const getDaysRemaining = (endDate: string): number => {
    const end = new Date(endDate);
    const now = new Date();
    const diff = end.getTime() - now.getTime();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  const formatDate = (date?: string): string => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('pt-BR');
  };

  const handleCopyOwnerEmail = async () => {
    const email = selectedOrgDetails?.ownerEmail;
    if (!email) return;

    try {
      await navigator.clipboard.writeText(email);
      setCopiedOwnerEmail(true);
      window.setTimeout(() => setCopiedOwnerEmail(false), 2000);
    } catch (err) {
      console.error('Error copying owner email:', err);
      window.prompt('Copie o email:', email);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="admin-loading">
          <div className="spinner"></div>
          <p>Carregando painel administrativo...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-container">
        {/* Header */}
        <header className="admin-header">
          <div>
            <h1>Painel Administrativo</h1>
            <p className="admin-header-subtitle">Gerenciamento de trials e organizacoes</p>
          </div>
          <button className="admin-logout-btn" onClick={handleLogout}>
            Sair
          </button>
        </header>

        <AdminSpecialistPrompts />

        {/* Active Trials */}
        <section className="admin-section">
          <h2>Trials Ativos ({activeTrials.length})</h2>

          {activeTrials.length === 0 ? (
            <p className="empty-state-admin">Nenhum trial ativo no momento.</p>
          ) : (
            <div className="trials-grid">
              {activeTrials.map(org => {
                const daysLeft = getDaysRemaining(org.trial!.endDate);
                return (
                  <div
                    key={org.organizationId}
                    className={`trial-card ${selectedOrgDetails?.organizationId === org.organizationId ? 'selected' : ''}`}
                    onClick={() => setSelectedOrgDetails(org)}
                  >
                    <div className="trial-card-header">
                      <div>
                        <h3>{org.name}</h3>
                        <p className="trial-card-slug">{org.slug}</p>
                      </div>
                      <span className={`trial-badge ${daysLeft === 0 ? 'expired' : ''}`}>
                        Trial ativo
                      </span>
                    </div>

                    <p className="trial-days-left">
                      {daysLeft > 0
                        ? `${daysLeft} dias restantes`
                        : 'Expira hoje'}
                      {' '} - Expira em {new Date(org.trial!.endDate).toLocaleDateString('pt-BR')}
                    </p>

                    <div className="trial-limits">
                      <span className="trial-limit-item">
                        <strong>{org.trial!.maxUsers}</strong> users
                      </span>
                      <span className="trial-limit-item">
                        <strong>{org.trial!.maxLeadsPerMonth}</strong> leads/mes
                      </span>
                      <span className="trial-limit-item">
                        <strong>{org.trial!.maxWhatsAppAccounts}</strong> WhatsApp
                      </span>
                    </div>

                    <Link
                      to={`/stats-dashboard?orgId=${org.organizationId}`}
                      className="trial-card-dashboard-link"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Ver Dashboard
                    </Link>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Search Organizations */}
        <section className="admin-section">
          <h2>Buscar Organizacoes</h2>

          <div className="search-box">
            <input
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder="Buscar por nome ou slug..."
            />
            <button
              className="btn btn-primary"
              onClick={handleSearch}
              disabled={searchLoading}
            >
              {searchLoading ? 'Buscando...' : 'Buscar'}
            </button>
          </div>

          {searchResults.length > 0 && (
            <div className="org-results">
              {searchResults.map(org => (
                <div
                  key={org.organizationId}
                  className={`org-result-card ${selectedOrgDetails?.organizationId === org.organizationId ? 'selected' : ''}`}
                  onClick={() => setSelectedOrgDetails(org)}
                >
                  <div className="org-result-info">
                    <div className="org-result-name">{org.name}</div>
                    <div className="org-result-details">
                      <span><code>{org.slug}</code></span>
                      <span className={`plan-badge-small ${org.planId === 'free' ? 'free' : 'paid'}`}>
                        {org.planId}
                      </span>
                      {org.ownerEmail && (
                        <span>Owner: {org.ownerName || org.ownerEmail}</span>
                      )}
                      {org.trial?.isActive && (
                        <span className="trial-badge">Trial ativo</span>
                      )}
                    </div>
                  </div>
                  <div className="org-result-actions">
                    <Link
                      to={`/stats-dashboard?orgId=${org.organizationId}`}
                      className="btn btn-primary btn-sm"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Ver Dashboard
                    </Link>
                    <button
                      className="btn btn-outline btn-sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedOrgDetails(org);
                      }}
                    >
                      Ver detalhes
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {searchTerm && searchResults.length === 0 && !searchLoading && (
            <p className="empty-state-admin">Nenhuma organizacao encontrada.</p>
          )}

          {selectedOrgDetails && (
            <div className="org-details-panel">
              <div className="org-details-header">
                <h3>Detalhes da organizacao</h3>
                <span className={`plan-badge-small ${selectedOrgDetails.planId === 'free' ? 'free' : 'paid'}`}>
                  {selectedOrgDetails.planId}
                </span>
              </div>

              <div className="org-details-grid">
                <div className="org-detail-item">
                  <span className="org-detail-label">Nome</span>
                  <strong>{selectedOrgDetails.name}</strong>
                </div>
                <div className="org-detail-item">
                  <span className="org-detail-label">Slug</span>
                  <strong><code>{selectedOrgDetails.slug}</code></strong>
                </div>
                <div className="org-detail-item">
                  <span className="org-detail-label">Status</span>
                  <strong>{selectedOrgDetails.status}</strong>
                </div>
                <div className="org-detail-item">
                  <span className="org-detail-label">Owner</span>
                  <strong>{selectedOrgDetails.ownerName || selectedOrgDetails.ownerEmail || '-'}</strong>
                </div>
                <div className="org-detail-item">
                  <span className="org-detail-label">Criada em</span>
                  <strong>{formatDate(selectedOrgDetails.createdAt)}</strong>
                </div>
              </div>

              <div className="org-contact-block">
                <span className="org-detail-label">Contato do dono</span>
                <div className="org-contact-row">
                  <span className="org-contact-email">
                    {selectedOrgDetails.ownerEmail || 'Sem email cadastrado'}
                  </span>
                  {selectedOrgDetails.ownerEmail && (
                    <button
                      className="btn btn-outline btn-sm org-copy-email-btn"
                      onClick={handleCopyOwnerEmail}
                    >
                      {copiedOwnerEmail ? 'Copiado!' : 'Copiar email'}
                    </button>
                  )}
                </div>
              </div>

              <div className="org-trial-details">
                <h4>Trial</h4>
                {selectedOrgDetails.trial?.isActive ? (
                  <>
                    <p className="org-trial-dates">
                      Ativa ate {formatDate(selectedOrgDetails.trial.endDate)} ({getDaysRemaining(selectedOrgDetails.trial.endDate)} dias restantes)
                    </p>
                    <div className="trial-limits">
                      <span className="trial-limit-item">
                        <strong>{selectedOrgDetails.trial.maxUsers}</strong> users
                      </span>
                      <span className="trial-limit-item">
                        <strong>{selectedOrgDetails.trial.maxLeadsPerMonth}</strong> leads/mes
                      </span>
                      <span className="trial-limit-item">
                        <strong>{selectedOrgDetails.trial.maxWhatsAppAccounts}</strong> WhatsApp
                      </span>
                    </div>
                  </>
                ) : (
                  <p className="org-trial-empty">Sem trial ativa para esta organizacao.</p>
                )}
              </div>

              <div className="org-details-actions">
                <Link
                  to={`/stats-dashboard?orgId=${selectedOrgDetails.organizationId}`}
                  className="btn btn-primary btn-sm"
                >
                  Ver Dashboard
                </Link>
                {selectedOrgDetails.planId === 'free' && !selectedOrgDetails.trial?.isActive && (
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => handleOpenActivateModal(selectedOrgDetails)}
                    disabled={actionLoading}
                  >
                    Ativar Trial
                  </button>
                )}

                {selectedOrgDetails.trial?.isActive && (
                  <>
                    <button
                      className="btn btn-outline btn-sm"
                      onClick={() => handleRenewTrial(selectedOrgDetails.organizationId)}
                      disabled={actionLoading}
                    >
                      Renovar
                    </button>
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => handleDeactivateTrial(selectedOrgDetails.organizationId)}
                      disabled={actionLoading}
                      style={{ color: '#DC2626' }}
                    >
                      Desativar
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </section>
      </div>

      {/* Activate Trial Modal */}
      {showActivateModal && selectedOrg && (
        <div className="admin-modal-overlay" onClick={() => setShowActivateModal(false)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Ativar Trial</h3>
              <button className="admin-modal-close" onClick={() => setShowActivateModal(false)}>
                &times;
              </button>
            </div>
            <div className="admin-modal-body">
              <div className="form-group">
                <label>Organizacao</label>
                <input type="text" value={selectedOrg.name} readOnly />
              </div>
              <div className="form-group">
                <label>Max. Usuarios</label>
                <input
                  type="number"
                  min={1}
                  value={trialMaxUsers}
                  onChange={e => setTrialMaxUsers(Number(e.target.value))}
                />
              </div>
              <div className="form-group">
                <label>Max. Leads/mes</label>
                <input
                  type="number"
                  min={1}
                  value={trialMaxLeads}
                  onChange={e => setTrialMaxLeads(Number(e.target.value))}
                />
              </div>
              <div className="form-group">
                <label>Max. Contas WhatsApp</label>
                <input
                  type="number"
                  min={1}
                  value={trialMaxWhatsApp}
                  onChange={e => setTrialMaxWhatsApp(Number(e.target.value))}
                />
              </div>
              <p style={{ color: 'var(--gray-500)', fontSize: '13px', marginTop: '8px' }}>
                O trial tera validade de 3 meses a partir de hoje.
              </p>
            </div>
            <div className="admin-modal-footer">
              <button
                className="btn btn-ghost"
                onClick={() => setShowActivateModal(false)}
                disabled={actionLoading}
              >
                Cancelar
              </button>
              <button
                className="btn btn-primary"
                onClick={handleActivateTrial}
                disabled={actionLoading}
              >
                {actionLoading ? 'Ativando...' : 'Ativar Trial'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
